// RestaurantReport.jsx - Complete with In-House Filter
import React, { useState, useEffect } from 'react';
import {
  FaRupeeSign,
  FaUtensils,
  FaWallet,
  FaCreditCard,
  FaMobileAlt,
  FaMoneyBillWave,
  FaTimes,
  FaPercent,
  FaChartLine,
  FaDownload,
  FaEnvelope,
  FaPrint,
  FaCalendarAlt,
  FaSync,
  FaSpinner,
  FaBuilding,
  FaClock,
  FaArrowUp,
  FaArrowDown,
  FaDollarSign,
  FaTrophy,
  FaChartPie,
  FaFilePdf,
  FaFileExcel,
  FaUser,
  FaChartBar,
  FaList,
  FaFilter,
  FaCheck,
  FaSearch,
  FaEye,
  FaShoppingBag,
  FaHotel,
  FaBox,
  FaCoins,
  FaUniversity,
  FaQrcode,
  FaReceipt,
  FaFileInvoice,
  FaUserTie,
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
} from 'recharts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Payment Method Config
const PAYMENT_METHODS = [
  { key: 'cash', label: 'Cash', icon: <FaMoneyBillWave />, color: '#10b981', bg: '#d1fae5' },
  { key: 'card', label: 'Card', icon: <FaCreditCard />, color: '#3b82f6', bg: '#dbeafe' },
  {
    key: 'netbanking',
    label: 'Netbanking',
    icon: <FaUniversity />,
    color: '#8b5cf6',
    bg: '#ede9fe',
  },
  {
    key: 'digital',
    label: 'Digital Payments',
    icon: <FaMobileAlt />,
    color: '#f59e0b',
    bg: '#fef3c7',
  },
  { key: 'upi', label: 'UPI', icon: <FaQrcode />, color: '#ec4899', bg: '#fce7f3' },
  { key: 'room_post', label: 'Room Post', icon: <FaHotel />, color: '#0f3460', bg: '#e0e7ff' },
  { key: 'other', label: 'Other', icon: <FaWallet />, color: '#6b7280', bg: '#f3f4f6' },
];

const PAYMENT_BUCKETS = ['cash', 'card', 'netbanking', 'digital', 'upi', 'room_post', 'other'];
const PAYMENT_LABEL = {
  cash: 'Cash',
  card: 'Card',
  netbanking: 'Netbanking',
  digital: 'Digital Payments',
  upi: 'UPI',
  room_post: 'Room Post',
  other: 'Other',
};
const PAYMENT_COLORS = {
  cash: '#10b981',
  card: '#3b82f6',
  netbanking: '#8b5cf6',
  digital: '#f59e0b',
  upi: '#ec4899',
  room_post: '#0f3460',
  other: '#6b7280',
};
const PAYMENT_BG_COLORS = {
  cash: '#d1fae5',
  card: '#dbeafe',
  netbanking: '#ede9fe',
  digital: '#fef3c7',
  upi: '#fce7f3',
  room_post: '#e0e7ff',
  other: '#f3f4f6',
};

const CHANNEL_LABEL = {
  dining: 'Dine-In',
  parcel: 'Parcel',
  room: 'Room Service',
  swiggy: 'Swiggy',
  zomato: 'Zomato',
  other_platform: 'Other Platform',
};

const CHANNEL_ORDER = ['dining', 'parcel', 'room', 'swiggy', 'zomato', 'other_platform'];
const CHANNEL_COLORS = {
  dining: '#1a1a2e',
  parcel: '#e94560',
  room: '#0f3460',
  swiggy: '#ff6b35',
  zomato: '#ee2a3b',
  other_platform: '#533483',
};

// In-House filter labels
const INHOUSE_FILTER_LABEL = {
  all: 'All Orders',
  inhouse: 'In-House Only',
  exclude: 'Exclude In-House',
};

// ================================================================
// Item Sales Report — Reusable table section (Food / Bar)
// ================================================================
const ItemSalesSection = ({ title, icon, accent, bg, rows, total, totalLabel, emptyText }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Section header */}
      <div className={`px-5 py-3 ${bg} border-b border-gray-200 flex items-center justify-between`}>
        <div className={`flex items-center gap-2 font-semibold text-sm ${accent}`}>
          {icon}
          <span>{title}</span>
          <span className="text-xs font-normal text-gray-500 ml-1">
            ({rows.length} item{rows.length !== 1 ? 's' : ''})
          </span>
        </div>
      </div>

      {/* Table */}
      {rows.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">{emptyText}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Item Name
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Qty Issued
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Qty Cnld
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Qty Sold
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Closing Stock
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-2.5 font-medium text-gray-900">{row['Item Name']}</td>
                  <td className="px-4 py-2.5 text-right text-gray-700">{row['Qty Issued']}</td>
                  <td className="px-4 py-2.5 text-right text-red-600 font-medium">
                    {row['Qty Cnld'] > 0 ? row['Qty Cnld'] : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-emerald-700">
                    {row['Qty Sold']}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-500">{row['Closing Stock']}</td>
                </tr>
              ))}
            </tbody>
            {total && (
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-900 uppercase text-xs tracking-wider">
                    {totalLabel}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">
                    {total['Qty Issued']}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red-700">
                    {total['Qty Cnld']}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-700">
                    {total['Qty Sold']}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-700">
                    {total['Closing Stock']}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

// ================================================================
// Item Sales Report — Preview (property header + food + bar + grand total)
// ================================================================
const ItemSalesReportPreview = ({ data }) => {
  const foodRows = data['Food Items'] || [];
  const foodTotal = data['Food Total'];
  const barRows = data['Bar Counter Items'] || [];
  const barTotal = data['Bar Counter Total'];
  const grandTotal = data['Grand Total'];

  return (
    <div className="space-y-5">
      {/* Property header card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="text-center">
          {data.property_group_name && (
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
              {data.property_group_name}
            </p>
          )}
          <h2 className="text-xl font-bold text-gray-900 uppercase mt-1">{data.property_name}</h2>
          {data.property_address && (
            <p className="text-xs text-gray-500 mt-1">{data.property_address}</p>
          )}
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
            {data.property_contact && <span>Ph: {data.property_contact}</span>}
            {data.property_gst && <span>GSTIN: {data.property_gst}</span>}
            {data.property_fssai && <span>FSSAI: {data.property_fssai}</span>}
            {data.property_pan && <span>PAN: {data.property_pan}</span>}
            {data.property_cin && <span>CIN: {data.property_cin}</span>}
            {data.property_state_code && <span>State: {data.property_state_code}</span>}
          </div>
        </div>

        <div className="border-t border-dashed border-gray-300 my-4" />

        <div className="flex flex-wrap justify-between items-center gap-2 text-xs text-gray-500">
          <span>
            Period:{' '}
            <b className="text-gray-700">
              {data.from_date === data.to_date
                ? new Date(data.from_date).toLocaleDateString('en-IN')
                : `${new Date(data.from_date).toLocaleDateString('en-IN')} → ${new Date(
                    data.to_date
                  ).toLocaleDateString('en-IN')}`}
            </b>
          </span>
          <span>
            Generated:{' '}
            <b className="text-gray-700">{new Date(data.generated_at).toLocaleString('en-IN')}</b>
          </span>
        </div>
      </div>

      {/* FOOD ITEMS */}
      <ItemSalesSection
        title="Food Items"
        icon={<FaUtensils />}
        accent="text-emerald-600"
        bg="bg-emerald-50"
        rows={foodRows}
        total={foodTotal}
        totalLabel="Food Total"
        emptyText="No food items in this period"
      />

      {/* BAR COUNTER ITEMS */}
      {barRows.length > 0 && (
        <ItemSalesSection
          title="Bar Counter Items"
          icon={<FaCoins />}
          accent="text-amber-600"
          bg="bg-amber-50"
          rows={barRows}
          total={barTotal}
          totalLabel="Bar Counter Total"
          emptyText=""
        />
      )}

      {/* GRAND TOTAL */}
      {grandTotal && (
        <div className="bg-gradient-to-r from-[#0a1628] to-[#1a2a4a] rounded-xl p-5 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-[#c9a84c]/20 rounded-xl flex items-center justify-center">
                <FaTrophy className="text-[#c9a84c] text-xl" />
              </div>
              <div>
                <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-wider">
                  Grand Total
                </p>
                <p className="text-xs text-gray-400 mt-0.5">All items combined (food + bar)</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 md:gap-6 text-right">
              <div>
                <p className="text-[10px] uppercase text-gray-400 tracking-wider">Issued</p>
                <p className="text-lg font-bold text-white">{grandTotal['Qty Issued']}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-gray-400 tracking-wider">Cnld</p>
                <p className="text-lg font-bold text-red-400">{grandTotal['Qty Cnld']}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-gray-400 tracking-wider">Sold</p>
                <p className="text-lg font-bold text-emerald-400">{grandTotal['Qty Sold']}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-gray-400 tracking-wider">Cls. Stock</p>
                <p className="text-lg font-bold text-white">{grandTotal['Closing Stock']}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RestaurantReport = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState('daily');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [selectedPaymentBucket, setSelectedPaymentBucket] = useState('all');
  const [inhouseFilter, setInhouseFilter] = useState('all'); // ← NEW
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [itemsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeChartTab, setActiveChartTab] = useState('trend');

  // Modal States
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailRecipientName, setEmailRecipientName] = useState('');
  const [emailCcHotel, setEmailCcHotel] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [exportFormat, setExportFormat] = useState('xlsx');

  const [emailPeriod, setEmailPeriod] = useState('daily');
  const [emailStartDate, setEmailStartDate] = useState('');
  const [emailEndDate, setEmailEndDate] = useState('');

  const [pdfPeriod, setPdfPeriod] = useState('daily');
  const [pdfStartDate, setPdfStartDate] = useState('');
  const [pdfEndDate, setPdfEndDate] = useState('');

  const [excelPeriod, setExcelPeriod] = useState('daily');
  const [excelStartDate, setExcelStartDate] = useState('');
  const [excelEndDate, setExcelEndDate] = useState('');

  // ============================================================
  // Item Sales Report — SINGLE modal with date picker + view + print
  // ============================================================
  const [showItemSalesModal, setShowItemSalesModal] = useState(false);
  const [itemSalesFromDate, setItemSalesFromDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [itemSalesToDate, setItemSalesToDate] = useState(new Date().toISOString().split('T')[0]);
  const [itemSalesPrinting, setItemSalesPrinting] = useState(false);

  // Preview state (loaded inside the same modal)
  const [itemSalesViewLoading, setItemSalesViewLoading] = useState(false);
  const [itemSalesViewData, setItemSalesViewData] = useState(null);
  const [itemSalesViewError, setItemSalesViewError] = useState(null);
  const [itemSalesHasFetched, setItemSalesHasFetched] = useState(false);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('period', filterType);

      if (filterType === 'daily' && selectedDate) {
        params.append('on_date', selectedDate);
      } else if (filterType === 'monthly') {
        params.append('on_date', `${year}-${String(month).padStart(2, '0')}-01`);
      } else if (filterType === 'yearly') {
        params.append('on_date', `${year}-01-01`);
      } else if (filterType === 'weekly' && selectedDate) {
        params.append('on_date', selectedDate);
      } else if (filterType === 'custom' && startDate && endDate) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }

      if (selectedChannel !== 'all') {
        params.append('channel', selectedChannel);
      }
      if (selectedPaymentBucket !== 'all') {
        params.append('payment_bucket', selectedPaymentBucket);
      }
      // ── In-house filter ──
      if (inhouseFilter !== 'all') {
        params.append('inhouse_filter', inhouseFilter);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      params.append('include_cancelled', 'true');

      const url = `${BQ_BASE_URL}/bq/api/restaurant/reports/dashboard?${params.toString()}`;
      console.log('Fetching report from:', url);

      const response = await fetch(url, {
        headers: { accept: 'application/json' },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Report data:', data);
        setReportData(data);
        setOrders(data.recent_orders || []);
        setTotalOrders(data.recent_orders_total || 0);
        toast.success('Report loaded successfully!');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fetch report');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error(error.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterType,
    selectedDate,
    year,
    month,
    selectedChannel,
    selectedPaymentBucket,
    inhouseFilter, // ← NEW
  ]);

  const handlePeriodChange = (type) => {
    setFilterType(type);
    setCurrentPage(1);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setCurrentPage(1);
  };

  const getFilteredOrders = () => {
    let filtered = orders;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          (order.order_no || '').toLowerCase().includes(query) ||
          (order.customer || '').toLowerCase().includes(query) ||
          (order.table || '').toLowerCase().includes(query) ||
          (order.room || '').toLowerCase().includes(query) ||
          (order.kot_number || '').toLowerCase().includes(query)
      );
    }
    return filtered;
  };

  const filteredOrders = getFilteredOrders();
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getPaymentDistribution = () => {
    if (!reportData?.payment_distribution?.breakdown) return [];
    return reportData.payment_distribution.breakdown
      .filter((item) => item.amount > 0)
      .map((item) => ({
        name: item.label,
        value: item.amount,
        percentage: item.percentage,
        color: PAYMENT_COLORS[item.bucket] || '#6b7280',
        bg: PAYMENT_BG_COLORS[item.bucket] || '#f3f4f6',
        key: item.bucket,
      }));
  };

  const getDailyTracking = () => {
    if (!reportData?.daily_tracking) return [];
    return reportData.daily_tracking;
  };

  const getChannelSummary = () => {
    if (!reportData?.channel_summary?.channels) return [];
    return reportData.channel_summary.channels.filter((ch) => ch.revenue > 0 || ch.order_count > 0);
  };

  const getKPIs = () => {
    if (!reportData?.kpis) return {};
    return reportData.kpis;
  };

  const getTopItems = () => {
    if (!reportData?.top_items) return [];
    return reportData.top_items;
  };

  const getHourlySales = () => {
    if (!reportData?.hourly_sales) return [];
    return reportData.hourly_sales;
  };

  const handleExport = async (format) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('period', filterType);
      params.append('export_format', format);

      if (filterType === 'daily' && selectedDate) {
        params.append('on_date', selectedDate);
      } else if (filterType === 'monthly') {
        params.append('on_date', `${year}-${String(month).padStart(2, '0')}-01`);
      } else if (filterType === 'yearly') {
        params.append('on_date', `${year}-01-01`);
      } else if (filterType === 'weekly' && selectedDate) {
        params.append('on_date', selectedDate);
      } else if (filterType === 'custom' && startDate && endDate) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }

      if (selectedChannel !== 'all') {
        params.append('channel', selectedChannel);
      }
      // ── In-house filter on export ──
      if (inhouseFilter !== 'all') {
        params.append('inhouse_filter', inhouseFilter);
      }
      params.append('include_cancelled', 'true');

      const url = `${BQ_BASE_URL}/bq/api/restaurant/reports/export?${params.toString()}`;

      const response = await fetch(url, {
        headers: { accept: '*/*' },
        credentials: 'include',
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        const contentDisposition = response.headers.get('content-disposition');
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1].replace(/"/g, '')
          : `Restaurant_Report.${format}`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        toast.success(`Report exported as ${format.toUpperCase()} successfully!`);
      } else {
        throw new Error('Failed to export report');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export report');
    } finally {
      setLoading(false);
      setShowPdfModal(false);
      setShowExcelModal(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailRecipient) {
      toast.error('Please enter recipient email');
      return;
    }

    setSendingEmail(true);
    try {
      const payload = {
        recipients: [emailRecipient],
        subject: 'Restaurant Report',
        message: 'Please find the restaurant report attached.',
        export_format: exportFormat || 'xlsx',
        period: emailPeriod,
      };

      if (emailPeriod === 'daily' && emailStartDate) {
        payload.on_date = emailStartDate;
      } else if (emailPeriod === 'weekly' && emailStartDate) {
        payload.on_date = emailStartDate;
      } else if (emailPeriod === 'monthly' && emailStartDate) {
        payload.on_date = emailStartDate;
      } else if (emailPeriod === 'custom' && emailStartDate && emailEndDate) {
        payload.start_date = emailStartDate;
        payload.end_date = emailEndDate;
      }

      if (selectedChannel !== 'all') {
        payload.channel = selectedChannel;
      }
      // ── In-house filter on email ──
      if (inhouseFilter !== 'all') {
        payload.inhouse_filter = inhouseFilter;
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/reports/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(`Report sent to ${emailRecipient} successfully!`);
        setShowEmailModal(false);
        setEmailRecipient('');
        setEmailRecipientName('');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      console.error('Email error:', error);
      toast.error(error.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // ============ ITEM SALES REPORT — PRINT ============
  const handlePrintItemSales = async () => {
    if (!itemSalesFromDate) {
      toast.error('Please select a From date');
      return;
    }
    if (!itemSalesToDate) {
      toast.error('Please select a To date');
      return;
    }
    if (new Date(itemSalesToDate) < new Date(itemSalesFromDate)) {
      toast.error('To date cannot be before From date');
      return;
    }

    setItemSalesPrinting(true);
    try {
      const params = new URLSearchParams();
      params.append('from_date', itemSalesFromDate);
      params.append('to_date', itemSalesToDate);
      if (reportData?.meta?.property_id) {
        params.append('property_id', reportData.meta.property_id);
      }

      const url = `${BQ_BASE_URL}/bq/api/reports/item-sales-detail/print?${params.toString()}`;
      console.log('Printing item sales report:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: { accept: 'application/json' },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        if (data.queued) {
          toast.success(
            `Print job queued! (${data.food_item_count || 0} food, ${data.bar_item_count || 0} bar)`
          );
        } else {
          toast.info('Print job submitted but could not be queued.');
        }
      } else {
        throw new Error(data.detail || 'Failed to queue print job');
      }
    } catch (error) {
      console.error('Item sales print error:', error);
      toast.error(error.message || 'Failed to print item sales report');
    } finally {
      setItemSalesPrinting(false);
    }
  };

  // ============ ITEM SALES REPORT — VIEW ============
  const handleViewItemSales = async () => {
    if (!itemSalesFromDate) {
      toast.error('Please select a From date');
      return;
    }
    if (!itemSalesToDate) {
      toast.error('Please select a To date');
      return;
    }
    if (new Date(itemSalesToDate) < new Date(itemSalesFromDate)) {
      toast.error('To date cannot be before From date');
      return;
    }

    setItemSalesViewLoading(true);
    setItemSalesViewError(null);
    setItemSalesViewData(null);

    try {
      const params = new URLSearchParams();
      params.append('from_date', itemSalesFromDate);
      params.append('to_date', itemSalesToDate);
      if (reportData?.meta?.property_id) {
        params.append('property_id', reportData.meta.property_id);
      }

      const url = `${BQ_BASE_URL}/bq/api/reports/item-sales-detail?${params.toString()}`;
      console.log('Viewing item sales report:', url);

      const response = await fetch(url, {
        headers: { accept: 'application/json' },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        setItemSalesViewData(data);
        setItemSalesHasFetched(true);
      } else {
        throw new Error(data.detail || 'Failed to load item sales report');
      }
    } catch (error) {
      console.error('Item sales view error:', error);
      setItemSalesViewError(error.message || 'Failed to load item sales report');
      toast.error(error.message || 'Failed to load item sales report');
    } finally {
      setItemSalesViewLoading(false);
    }
  };

  const openItemSalesModal = () => {
    const d = selectedDate || new Date().toISOString().split('T')[0];
    setItemSalesFromDate(d);
    setItemSalesToDate(d);
    setItemSalesViewData(null);
    setItemSalesViewError(null);
    setItemSalesHasFetched(false);
    setShowItemSalesModal(true);

    setTimeout(() => {
      handleViewItemSalesFor(d, d);
    }, 0);
  };

  const handleViewItemSalesFor = async (fromOverride, toOverride) => {
    const from = fromOverride ?? itemSalesFromDate;
    const to = toOverride ?? itemSalesToDate;

    if (!from || !to) {
      toast.error('Please select both From and To dates');
      return;
    }
    if (new Date(to) < new Date(from)) {
      toast.error('To date cannot be before From date');
      return;
    }

    setItemSalesViewLoading(true);
    setItemSalesViewError(null);
    setItemSalesViewData(null);

    try {
      const params = new URLSearchParams();
      params.append('from_date', from);
      params.append('to_date', to);
      if (reportData?.meta?.property_id) {
        params.append('property_id', reportData.meta.property_id);
      }

      const url = `${BQ_BASE_URL}/bq/api/reports/item-sales-detail?${params.toString()}`;

      const response = await fetch(url, {
        headers: { accept: 'application/json' },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        setItemSalesViewData(data);
        setItemSalesHasFetched(true);
      } else {
        throw new Error(data.detail || 'Failed to load item sales report');
      }
    } catch (error) {
      console.error('Item sales view error:', error);
      setItemSalesViewError(error.message || 'Failed to load item sales report');
      toast.error(error.message || 'Failed to load item sales report');
    } finally {
      setItemSalesViewLoading(false);
    }
  };

  const resetItemSalesPreview = () => {
    setItemSalesViewData(null);
    setItemSalesViewError(null);
    setItemSalesHasFetched(false);
  };

  const resetFilters = () => {
    setSelectedChannel('all');
    setSelectedPaymentBucket('all');
    setInhouseFilter('all'); // ← NEW
    setSearchQuery('');
    toast.info('Filters reset');
  };

  const kpis = getKPIs();
  const paymentData = getPaymentDistribution();
  const dailyData = getDailyTracking();
  const channelData = getChannelSummary();
  const topItems = getTopItems();
  const hourlyData = getHourlySales();

  const getTodayPaymentBreakdown = () => {
    if (!dailyData || dailyData.length === 0) return [];
    const today = dailyData[dailyData.length - 1];
    if (!today) return [];

    const breakdown = [];
    PAYMENT_BUCKETS.forEach((bucket) => {
      if (today[bucket] && today[bucket] > 0) {
        breakdown.push({
          key: bucket,
          label: PAYMENT_LABEL[bucket],
          amount: today[bucket],
          color: PAYMENT_COLORS[bucket],
          bg: PAYMENT_BG_COLORS[bucket],
        });
      }
    });
    return breakdown;
  };

  const todayPayments = getTodayPaymentBreakdown();

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 font-sans">
      <ToastContainer position="bottom-right" autoClose={3000} />

      {/* Header */}
      <div className="bg-gradient-to-r from-[#0a1628] to-[#1a2a4a] rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#c9a84c]/20 backdrop-blur rounded-xl flex items-center justify-center">
              <FaUtensils className="text-[#c9a84c] text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Restaurant Report</h1>
              <p className="text-[#c9a84c] text-sm">
                Complete analytics & insights for your restaurant operations
              </p>
              {reportData?.meta && (
                <p className="text-xs text-gray-400 mt-1">
                  {reportData.meta.label} • {reportData.meta.start_date} to{' '}
                  {reportData.meta.end_date}
                </p>
              )}
              {/* ── In-House filter badge ── */}
              {inhouseFilter !== 'all' && (
                <span
                  className={`inline-block mt-2 text-xs font-semibold px-2 py-0.5 rounded ${
                    inhouseFilter === 'inhouse'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {inhouseFilter === 'inhouse' ? '● In-House Only' : '○ Excluding In-House'}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
            <button
              onClick={openItemSalesModal}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium"
            >
              <FaReceipt /> Item Sales
            </button>

            <button
              onClick={() => {
                setExcelPeriod(filterType);
                setShowExcelModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-sm font-medium"
            >
              <FaFileExcel /> Excel
            </button>
            <button
              onClick={() => {
                setPdfPeriod(filterType);
                setShowPdfModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
            >
              <FaFilePdf /> PDF
            </button>
            <button
              onClick={() => {
                setEmailPeriod(filterType);
                setShowEmailModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#c9a84c] text-[#0a1628] rounded-lg hover:bg-[#b8973a] transition text-sm font-medium"
            >
              <FaEnvelope /> Email
            </button>
            <button
              onClick={fetchReportData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition text-sm font-medium backdrop-blur"
            >
              <FaSync className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
            {['daily', 'weekly', 'monthly', 'yearly', 'custom'].map((type) => (
              <button
                key={type}
                onClick={() => handlePeriodChange(type)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition ${
                  filterType === type
                    ? 'bg-white shadow-sm text-[#0a1628]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          {filterType === 'daily' && (
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
            />
          )}

          {filterType === 'weekly' && (
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
            />
          )}

          {filterType === 'monthly' && (
            <>
              <select
                value={year}
                onChange={(e) => {
                  setYear(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <select
                value={month}
                onChange={(e) => {
                  setMonth(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
              >
                {[
                  'Jan',
                  'Feb',
                  'Mar',
                  'Apr',
                  'May',
                  'Jun',
                  'Jul',
                  'Aug',
                  'Sep',
                  'Oct',
                  'Nov',
                  'Dec',
                ].map((m, i) => (
                  <option key={i} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </>
          )}

          {filterType === 'yearly' && (
            <select
              value={year}
              onChange={(e) => {
                setYear(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          )}

          {filterType === 'custom' && (
            <>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
              />
            </>
          )}

          {/* ── In-House filter (quick access) ── */}
          <select
            value={inhouseFilter}
            onChange={(e) => {
              setInhouseFilter(e.target.value);
              setCurrentPage(1);
            }}
            className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c] ${
              inhouseFilter === 'inhouse'
                ? 'bg-amber-50 border-amber-400 text-amber-800 font-medium'
                : inhouseFilter === 'exclude'
                  ? 'bg-gray-100 border-gray-400 text-gray-700 font-medium'
                  : 'border-gray-300 bg-white'
            }`}
          >
            <option value="all">All Orders</option>
            <option value="inhouse">In-House Only</option>
            <option value="exclude">Exclude In-House</option>
          </select>

          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition"
            >
              <FaFilter /> Filter
              {(selectedChannel !== 'all' || selectedPaymentBucket !== 'all') && (
                <span className="w-2 h-2 bg-[#c9a84c] rounded-full animate-pulse"></span>
              )}
            </button>
          </div>
        </div>

        {showFilterDropdown && (
          <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-2">
                  Channel
                </label>
                <select
                  value={selectedChannel}
                  onChange={(e) => {
                    setSelectedChannel(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c] bg-white"
                >
                  <option value="all">All Channels</option>
                  {CHANNEL_ORDER.map((ch) => (
                    <option key={ch} value={ch}>
                      {CHANNEL_LABEL[ch] || ch}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-2">
                  Payment Method
                </label>
                <select
                  value={selectedPaymentBucket}
                  onChange={(e) => {
                    setSelectedPaymentBucket(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c] bg-white"
                >
                  <option value="all">All Methods</option>
                  {PAYMENT_BUCKETS.map((bucket) => (
                    <option key={bucket} value={bucket}>
                      {PAYMENT_LABEL[bucket] || bucket}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-2">
                  Guest Type
                </label>
                <select
                  value={inhouseFilter}
                  onChange={(e) => {
                    setInhouseFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c] bg-white"
                >
                  <option value="all">All Orders</option>
                  <option value="inhouse">In-House Only</option>
                  <option value="exclude">Exclude In-House</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition flex items-center gap-2"
                >
                  <FaTimes /> Clear All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && !reportData ? (
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#0a1628] border-t-[#c9a84c] rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading report...</p>
          </div>
        </div>
      ) : !reportData ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-200">
          <FaChartLine className="text-5xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Data Available</h3>
          <p className="text-gray-400">Try adjusting your filters or refresh the page</p>
        </div>
      ) : (
        <>
          {/* ===== KPI CARDS ===== */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-[#0a1628] to-[#1a2a4a] rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-[#c9a84c] uppercase tracking-wider">
                    Total Revenue
                  </p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {formatCurrency(kpis.total_revenue || 0)}
                  </p>
                  {kpis.revenue_growth_pct !== null && (
                    <p
                      className={`text-xs mt-1 flex items-center gap-1 ${
                        kpis.revenue_growth_pct >= 0 ? 'text-emerald-400' : 'text-red-400'
                      }`}
                    >
                      {kpis.revenue_growth_pct >= 0 ? (
                        <FaArrowUp size={10} />
                      ) : (
                        <FaArrowDown size={10} />
                      )}
                      {Math.abs(kpis.revenue_growth_pct)}% vs last period
                    </p>
                  )}
                </div>
                <div className="w-12 h-12 bg-[#c9a84c]/20 rounded-2xl flex items-center justify-center">
                  <FaRupeeSign className="text-[#c9a84c] text-2xl" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-all transform hover:-translate-y-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    GST Collected
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatCurrency(kpis.gst_collected || 0)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {kpis.gst_pct_of_revenue || 0}% of revenue
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
                  <FaPercent className="text-purple-600 text-2xl" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-all transform hover:-translate-y-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Orders
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{kpis.total_orders || 0}</p>
                  {kpis.orders_growth_pct !== null && (
                    <p
                      className={`text-xs mt-1 flex items-center gap-1 ${
                        kpis.orders_growth_pct >= 0 ? 'text-emerald-600' : 'text-red-600'
                      }`}
                    >
                      {kpis.orders_growth_pct >= 0 ? (
                        <FaArrowUp size={10} />
                      ) : (
                        <FaArrowDown size={10} />
                      )}
                      {Math.abs(kpis.orders_growth_pct)}% vs last period
                    </p>
                  )}
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center">
                  <FaShoppingBag className="text-orange-600 text-2xl" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-all transform hover:-translate-y-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Order Value
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatCurrency(kpis.avg_order_value || 0)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {kpis.total_items_sold || 0} items sold
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center">
                  <FaDollarSign className="text-emerald-600 text-2xl" />
                </div>
              </div>
            </div>
          </div>

          {/* ===== TODAY'S PAYMENT BREAKDOWN ===== */}
          {todayPayments.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <FaCoins className="text-[#c9a84c]" />
                  Today's Payment Breakdown
                  <span className="text-xs font-normal text-gray-400 ml-2">
                    {new Date(selectedDate).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </h3>
                <span className="text-xs text-gray-500">
                  Total: {formatCurrency(todayPayments.reduce((sum, p) => sum + p.amount, 0))}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {todayPayments.map((payment) => (
                  <div
                    key={payment.key}
                    className="rounded-xl p-4 border transition-all hover:shadow-md"
                    style={{
                      backgroundColor: payment.bg || '#f9fafb',
                      borderColor: payment.color + '40',
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: payment.color }}
                      >
                        {payment.key === 'cash' && <FaMoneyBillWave size={18} />}
                        {payment.key === 'card' && <FaCreditCard size={18} />}
                        {payment.key === 'netbanking' && <FaUniversity size={18} />}
                        {payment.key === 'digital' && <FaMobileAlt size={18} />}
                        {payment.key === 'upi' && <FaQrcode size={18} />}
                        {payment.key === 'room_post' && <FaHotel size={18} />}
                        {payment.key === 'other' && <FaWallet size={18} />}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">{payment.label}</p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(payment.amount)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== PAYMENT METHODS SUMMARY ===== */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
            {paymentData.map((item) => (
              <div
                key={item.key}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-all transform hover:-translate-y-0.5"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: item.bg || '#f3f4f6' }}
                  >
                    <span style={{ color: item.color }}>
                      {item.key === 'cash' && <FaMoneyBillWave size={18} />}
                      {item.key === 'card' && <FaCreditCard size={18} />}
                      {item.key === 'netbanking' && <FaUniversity size={18} />}
                      {item.key === 'digital' && <FaMobileAlt size={18} />}
                      {item.key === 'upi' && <FaQrcode size={18} />}
                      {item.key === 'room_post' && <FaHotel size={18} />}
                      {item.key === 'other' && <FaWallet size={18} />}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 truncate">{item.name}</p>
                    <p className="text-sm font-bold text-gray-900">{formatCurrency(item.value)}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <div className="flex-1 bg-gray-100 rounded-full h-1">
                        <div
                          className="rounded-full h-1 transition-all"
                          style={{
                            width: `${Math.min(item.percentage, 100)}%`,
                            backgroundColor: item.color,
                          }}
                        />
                      </div>
                      <span className="text-[10px] text-gray-400">{item.percentage}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Beautiful Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <FaChartLine className="text-[#c9a84c]" />
                  Revenue Trend
                </h3>
                <div className="flex gap-1 bg-gray-100 p-0.5 rounded-lg">
                  <button
                    onClick={() => setActiveChartTab('trend')}
                    className={`px-2 py-0.5 text-xs rounded transition ${activeChartTab === 'trend' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                  >
                    Trend
                  </button>
                  <button
                    onClick={() => setActiveChartTab('hourly')}
                    className={`px-2 py-0.5 text-xs rounded transition ${activeChartTab === 'hourly' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                  >
                    Hourly
                  </button>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  {activeChartTab === 'trend' ? (
                    <AreaChart data={dailyData}>
                      <defs>
                        <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#c9a84c" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#c9a84c" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
                      <YAxis
                        tickFormatter={(v) => `₹${v / 1000}K`}
                        stroke="#94a3b8"
                        fontSize={11}
                      />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Area
                        type="monotone"
                        dataKey="total"
                        stroke="#c9a84c"
                        strokeWidth={3}
                        fill="url(#revenueGradient)"
                        name="Revenue"
                      />
                    </AreaChart>
                  ) : (
                    <BarChart data={hourlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="label" stroke="#94a3b8" fontSize={10} />
                      <YAxis
                        tickFormatter={(v) => `₹${v / 1000}K`}
                        stroke="#94a3b8"
                        fontSize={11}
                      />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="revenue" fill="#c9a84c" radius={[4, 4, 0, 0]} name="Revenue" />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <FaChartPie className="text-[#c9a84c]" />
                Payment Distribution
              </h3>
              <div className="flex flex-col lg:flex-row items-center">
                <div className="w-full lg:w-1/2 h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={paymentData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {paymentData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            stroke="white"
                            strokeWidth={2}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-full lg:w-1/2 space-y-1.5">
                  {paymentData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-gray-600">{item.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900">
                          {formatCurrency(item.value)}
                        </span>
                        <span className="text-xs text-gray-400">{item.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Daily Payment Tracking */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <FaChartBar className="text-[#c9a84c]" />
              Daily Payment Tracking
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
                  <YAxis tickFormatter={(v) => `₹${v / 1000}K`} stroke="#94a3b8" fontSize={11} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  {PAYMENT_BUCKETS.filter((b) => dailyData.some((d) => d[b] > 0)).map((bucket) => (
                    <Bar
                      key={bucket}
                      dataKey={bucket}
                      stackId="a"
                      fill={PAYMENT_COLORS[bucket]}
                      name={PAYMENT_LABEL[bucket]}
                      radius={[4, 4, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Channel Performance */}
          {channelData.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <FaChartBar className="text-[#c9a84c]" />
                Channel Performance
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                        Channel
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                        Orders
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                        Revenue
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                        Tax
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                        Discount
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                        AOV
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                        Share
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {channelData.map((channel) => (
                      <tr key={channel.channel} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-2.5 font-medium text-gray-900 flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{
                              backgroundColor: CHANNEL_COLORS[channel.channel] || '#6b7280',
                            }}
                          />
                          {channel.label}
                        </td>
                        <td className="px-4 py-2.5 text-right">{channel.order_count}</td>
                        <td className="px-4 py-2.5 text-right font-semibold">
                          {formatCurrency(channel.revenue)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {formatCurrency(channel.tax_amount)}
                        </td>
                        <td className="px-4 py-2.5 text-right text-green-600">
                          {formatCurrency(channel.discount)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {formatCurrency(channel.avg_order_value)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="bg-gray-100 px-2 py-1 rounded-full text-xs font-medium">
                            {channel.revenue_share_pct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top Items */}
          {topItems.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <FaTrophy className="text-[#c9a84c]" />
                Top Selling Items
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                        #
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                        Item
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                        Qty
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                        Revenue
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                        Tax
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                        Channels
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {topItems.slice(0, 10).map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                              index === 0
                                ? 'bg-[#c9a84c] text-white'
                                : index === 1
                                  ? 'bg-gray-300 text-gray-700'
                                  : index === 2
                                    ? 'bg-amber-600 text-white'
                                    : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{item.item}</td>
                        <td className="px-4 py-2.5 text-right font-medium">{item.quantity}</td>
                        <td className="px-4 py-2.5 text-right font-semibold">
                          {formatCurrency(item.revenue)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {formatCurrency(item.tax_amount)}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex gap-1 flex-wrap">
                            {item.channels.map((ch) => (
                              <span
                                key={ch}
                                className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600"
                              >
                                {CHANNEL_LABEL[ch] || ch}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent Orders Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FaList className="text-[#c9a84c]" />
                Recent Orders
                {inhouseFilter === 'inhouse' && (
                  <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <FaUserTie size={10} /> In-House Only
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    placeholder="Search orders..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
                  />
                </div>
                <span className="text-xs text-gray-500">
                  {filteredOrders.length} of {totalOrders} orders
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      Order
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      Channel
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      Table/Room
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                      Total
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                      Tax
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      Payment
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedOrders.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-8 text-gray-500">
                        No orders found
                      </td>
                    </tr>
                  ) : (
                    paginatedOrders.map((order) => (
                      <tr
                        key={order.order_id}
                        className={`hover:bg-gray-50 transition ${
                          order.is_in_house ? 'bg-amber-50/40' : ''
                        }`}
                      >
                        <td className="px-4 py-2.5">
                          <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">
                            {order.order_no || order.order_id?.slice(0, 8)}
                          </span>
                          {order.kot_number && (
                            <div className="text-xs text-gray-400">KOT: {order.kot_number}</div>
                          )}
                          {order.is_in_house && (
                            <div className="text-[10px] mt-0.5 inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold uppercase">
                              <FaUserTie size={8} /> In-House
                              {order.in_house_reason && (
                                <span className="font-normal normal-case">
                                  · {order.in_house_reason}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-gray-900">
                            {order.customer || 'Guest'}
                          </div>
                          {order.customer_phone && (
                            <div className="text-xs text-gray-500">{order.customer_phone}</div>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                            style={{
                              backgroundColor: CHANNEL_COLORS[order.channel] || '#6b7280',
                            }}
                          >
                            {order.channel_label || order.channel}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          {order.table || order.room || order.parcel_number || '-'}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold">
                          {formatCurrency(order.total)}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-500">
                          {formatCurrency(order.tax)}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="flex items-center gap-1.5 text-gray-700">
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{
                                backgroundColor: PAYMENT_COLORS[order.payment_bucket] || '#6b7280',
                              }}
                            />
                            {order.payment_label || 'Not Paid'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {order.is_cancelled ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Cancelled
                            </span>
                          ) : order.is_paid ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                              Paid
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              {order.status || 'Pending'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs">
                          {order.ordered_at ? new Date(order.ordered_at).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 0 && (
              <div className="px-5 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition text-sm"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition ${
                          currentPage === page
                            ? 'bg-[#0a1628] text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  {totalPages > 5 && <span className="px-2 py-1.5 text-gray-400">...</span>}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ========== PDF MODAL ========== */}
      {showPdfModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FaFilePdf className="text-red-600" />
                Download PDF Report
              </h2>
              <button
                onClick={() => setShowPdfModal(false)}
                className="text-gray-400 hover:text-gray-600 transition p-1"
              >
                <FaTimes size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Download the complete restaurant report as a PDF document.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPdfModal(false)}
                className="flex-1 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleExport('pdf')}
                disabled={loading}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50"
              >
                {loading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                {loading ? 'Generating...' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== EXCEL MODAL ========== */}
      {showExcelModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FaFileExcel className="text-emerald-600" />
                Download Excel Report
              </h2>
              <button
                onClick={() => setShowExcelModal(false)}
                className="text-gray-400 hover:text-gray-600 transition p-1"
              >
                <FaTimes size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Download the complete restaurant report as an Excel spreadsheet.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExcelModal(false)}
                className="flex-1 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleExport('xlsx')}
                disabled={loading}
                className="flex-1 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50"
              >
                {loading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                {loading ? 'Generating...' : 'Download Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== EMAIL MODAL ========== */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FaEnvelope className="text-[#c9a84c]" />
                Send Report via Email
              </h2>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition p-1"
              >
                <FaTimes size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
                  Recipient Email *
                </label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
                  Recipient Name
                </label>
                <input
                  type="text"
                  placeholder="Enter recipient name"
                  value={emailRecipientName}
                  onChange={(e) => setEmailRecipientName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
                  Report Period
                </label>
                <select
                  value={emailPeriod}
                  onChange={(e) => setEmailPeriod(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
              {emailPeriod === 'custom' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={emailStartDate}
                      onChange={(e) => setEmailStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={emailEndDate}
                      onChange={(e) => setEmailEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
                  Export Format
                </label>
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
                >
                  <option value="xlsx">Excel (XLSX)</option>
                  <option value="pdf">PDF</option>
                  <option value="csv">CSV</option>
                </select>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEmailCcHotel(!emailCcHotel)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    emailCcHotel ? 'bg-[#0a1628] text-white' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {emailCcHotel ? '✓' : '○'} CC Hotel
                </button>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="flex-1 py-3 bg-[#0a1628] text-white rounded-xl hover:bg-[#1a2a4a] transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50"
              >
                {sendingEmail ? <FaSpinner className="animate-spin" /> : <FaEnvelope />}
                {sendingEmail ? 'Sending...' : 'Send Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          ITEM SALES MODAL
          ============================================================ */}
      {showItemSalesModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-5xl w-full shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
            <div className="flex flex-wrap justify-between items-center gap-3 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-purple-100 rounded-xl flex items-center justify-center">
                  <FaReceipt className="text-purple-600 text-xl" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Item Sales Report</h2>
                  <p className="text-xs text-gray-500">
                    View and print Qty Issued / Qty Cnld / Qty Sold per item
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowItemSalesModal(false)}
                disabled={itemSalesPrinting}
                className="text-gray-400 hover:text-gray-600 transition p-2 disabled:opacity-50"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="px-6 py-4 bg-white border-b border-gray-200">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[160px]">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
                    From Date *
                  </label>
                  <div className="relative">
                    <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
                    <input
                      type="date"
                      value={itemSalesFromDate}
                      onChange={(e) => {
                        setItemSalesFromDate(e.target.value);
                        resetItemSalesPreview();
                      }}
                      disabled={itemSalesPrinting || itemSalesViewLoading}
                      className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm disabled:bg-gray-50"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-[160px]">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
                    To Date *
                  </label>
                  <div className="relative">
                    <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
                    <input
                      type="date"
                      value={itemSalesToDate}
                      onChange={(e) => {
                        setItemSalesToDate(e.target.value);
                        resetItemSalesPreview();
                      }}
                      disabled={itemSalesPrinting || itemSalesViewLoading}
                      className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm disabled:bg-gray-50"
                    />
                  </div>
                </div>

                <button
                  onClick={() => handleViewItemSales()}
                  disabled={itemSalesViewLoading || itemSalesPrinting}
                  className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50"
                >
                  {itemSalesViewLoading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaEye />
                      View
                    </>
                  )}
                </button>

                <button
                  onClick={handlePrintItemSales}
                  disabled={itemSalesPrinting || itemSalesViewLoading}
                  className="px-5 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50"
                >
                  {itemSalesPrinting ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Printing...
                    </>
                  ) : (
                    <>
                      <FaPrint />
                      Print
                    </>
                  )}
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => {
                    const t = new Date().toISOString().split('T')[0];
                    setItemSalesFromDate(t);
                    setItemSalesToDate(t);
                    resetItemSalesPreview();
                    setTimeout(() => handleViewItemSalesFor(t, t), 0);
                  }}
                  disabled={itemSalesPrinting || itemSalesViewLoading}
                  className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition disabled:opacity-50"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const t = new Date();
                    const y = new Date(t);
                    y.setDate(t.getDate() - 1);
                    const d = y.toISOString().split('T')[0];
                    setItemSalesFromDate(d);
                    setItemSalesToDate(d);
                    resetItemSalesPreview();
                    setTimeout(() => handleViewItemSalesFor(d, d), 0);
                  }}
                  disabled={itemSalesPrinting || itemSalesViewLoading}
                  className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition disabled:opacity-50"
                >
                  Yesterday
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const end = new Date();
                    const start = new Date();
                    start.setDate(end.getDate() - 6);
                    const s = start.toISOString().split('T')[0];
                    const e = end.toISOString().split('T')[0];
                    setItemSalesFromDate(s);
                    setItemSalesToDate(e);
                    resetItemSalesPreview();
                    setTimeout(() => handleViewItemSalesFor(s, e), 0);
                  }}
                  disabled={itemSalesPrinting || itemSalesViewLoading}
                  className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition disabled:opacity-50"
                >
                  Last 7 Days
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    const start = new Date(now.getFullYear(), now.getMonth(), 1);
                    const s = start.toISOString().split('T')[0];
                    const e = now.toISOString().split('T')[0];
                    setItemSalesFromDate(s);
                    setItemSalesToDate(e);
                    resetItemSalesPreview();
                    setTimeout(() => handleViewItemSalesFor(s, e), 0);
                  }}
                  disabled={itemSalesPrinting || itemSalesViewLoading}
                  className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition disabled:opacity-50"
                >
                  This Month
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {itemSalesViewLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <FaSpinner className="animate-spin text-purple-600 text-4xl mb-4" />
                  <p className="text-gray-500 text-sm">Loading report...</p>
                </div>
              ) : itemSalesViewError ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <FaTimes className="text-red-600 text-2xl" />
                  </div>
                  <p className="text-gray-700 font-medium">Failed to load report</p>
                  <p className="text-gray-400 text-sm mt-1">{itemSalesViewError}</p>
                  <button
                    onClick={() => handleViewItemSales()}
                    className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
                  >
                    Retry
                  </button>
                </div>
              ) : itemSalesViewData ? (
                <ItemSalesReportPreview data={itemSalesViewData} />
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <FaEye className="text-purple-600 text-2xl" />
                  </div>
                  <p className="text-gray-700 font-medium">No preview loaded</p>
                  <p className="text-gray-400 text-sm mt-1 max-w-md">
                    Pick a date range above and click <b>View</b> to preview the item sales report,
                    or click <b>Print</b> to send it directly to the thermal printer.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantReport;

// // RestaurantReport.jsx - Complete with Beautiful Cards & Daily Payment Breakdown
// import React, { useState, useEffect } from 'react';
// import {
//   FaRupeeSign,
//   FaUtensils,
//   FaWallet,
//   FaCreditCard,
//   FaMobileAlt,
//   FaMoneyBillWave,
//   FaTimes,
//   FaPercent,
//   FaChartLine,
//   FaDownload,
//   FaEnvelope,
//   FaPrint,
//   FaCalendarAlt,
//   FaSync,
//   FaSpinner,
//   FaBuilding,
//   FaClock,
//   FaArrowUp,
//   FaArrowDown,
//   FaDollarSign,
//   FaTrophy,
//   FaChartPie,
//   FaFilePdf,
//   FaFileExcel,
//   FaUser,
//   FaChartBar,
//   FaList,
//   FaFilter,
//   FaCheck,
//   FaSearch,
//   FaEye,
//   FaShoppingBag,
//   FaHotel,
//   FaBox,
//   FaTrendUp,
//   FaTrendDown,
//   FaCoins,
//   FaUniversity,
//   FaQrcode,
// } from 'react-icons/fa';
// import {
//   BarChart,
//   Bar,
//   PieChart,
//   Pie,
//   Cell,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend,
//   ResponsiveContainer,
//   LineChart,
//   Line,
//   AreaChart,
//   Area,
//   ComposedChart,
// } from 'recharts';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// // Payment Method Config
// const PAYMENT_METHODS = [
//   { key: 'cash', label: 'Cash', icon: <FaMoneyBillWave />, color: '#10b981', bg: '#d1fae5' },
//   { key: 'card', label: 'Card', icon: <FaCreditCard />, color: '#3b82f6', bg: '#dbeafe' },
//   {
//     key: 'netbanking',
//     label: 'Netbanking',
//     icon: <FaUniversity />,
//     color: '#8b5cf6',
//     bg: '#ede9fe',
//   },
//   {
//     key: 'digital',
//     label: 'Digital Payments',
//     icon: <FaMobileAlt />,
//     color: '#f59e0b',
//     bg: '#fef3c7',
//   },
//   { key: 'upi', label: 'UPI', icon: <FaQrcode />, color: '#ec4899', bg: '#fce7f3' },
//   { key: 'room_post', label: 'Room Post', icon: <FaHotel />, color: '#0f3460', bg: '#e0e7ff' },
//   { key: 'other', label: 'Other', icon: <FaWallet />, color: '#6b7280', bg: '#f3f4f6' },
// ];

// const PAYMENT_BUCKETS = ['cash', 'card', 'netbanking', 'digital', 'upi', 'room_post', 'other'];
// const PAYMENT_LABEL = {
//   cash: 'Cash',
//   card: 'Card',
//   netbanking: 'Netbanking',
//   digital: 'Digital Payments',
//   upi: 'UPI',
//   room_post: 'Room Post',
//   other: 'Other',
// };
// const PAYMENT_COLORS = {
//   cash: '#10b981',
//   card: '#3b82f6',
//   netbanking: '#8b5cf6',
//   digital: '#f59e0b',
//   upi: '#ec4899',
//   room_post: '#0f3460',
//   other: '#6b7280',
// };
// const PAYMENT_BG_COLORS = {
//   cash: '#d1fae5',
//   card: '#dbeafe',
//   netbanking: '#ede9fe',
//   digital: '#fef3c7',
//   upi: '#fce7f3',
//   room_post: '#e0e7ff',
//   other: '#f3f4f6',
// };

// const CHANNEL_LABEL = {
//   dining: 'Dine-In',
//   parcel: 'Parcel',
//   room: 'Room Service',
//   swiggy: 'Swiggy',
//   zomato: 'Zomato',
//   other_platform: 'Other Platform',
// };

// const CHANNEL_ORDER = ['dining', 'parcel', 'room', 'swiggy', 'zomato', 'other_platform'];
// const CHANNEL_COLORS = {
//   dining: '#1a1a2e',
//   parcel: '#e94560',
//   room: '#0f3460',
//   swiggy: '#ff6b35',
//   zomato: '#ee2a3b',
//   other_platform: '#533483',
// };

// const RestaurantReport = () => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const [filterType, setFilterType] = useState('daily');
//   const [year, setYear] = useState(new Date().getFullYear());
//   const [month, setMonth] = useState(new Date().getMonth() + 1);
//   const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
//   const [startDate, setStartDate] = useState('');
//   const [endDate, setEndDate] = useState('');
//   const [selectedChannel, setSelectedChannel] = useState('all');
//   const [selectedPaymentBucket, setSelectedPaymentBucket] = useState('all');
//   const [showFilterDropdown, setShowFilterDropdown] = useState(false);
//   const [reportData, setReportData] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [orders, setOrders] = useState([]);
//   const [totalOrders, setTotalOrders] = useState(0);
//   const [itemsPerPage] = useState(10);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [activeChartTab, setActiveChartTab] = useState('trend');

//   // Modal States
//   const [showEmailModal, setShowEmailModal] = useState(false);
//   const [showPdfModal, setShowPdfModal] = useState(false);
//   const [showExcelModal, setShowExcelModal] = useState(false);
//   const [emailRecipient, setEmailRecipient] = useState('');
//   const [emailRecipientName, setEmailRecipientName] = useState('');
//   const [emailCcHotel, setEmailCcHotel] = useState(true);
//   const [sendingEmail, setSendingEmail] = useState(false);
//   const [exportFormat, setExportFormat] = useState('xlsx');

//   const [emailPeriod, setEmailPeriod] = useState('daily');
//   const [emailStartDate, setEmailStartDate] = useState('');
//   const [emailEndDate, setEmailEndDate] = useState('');

//   const [pdfPeriod, setPdfPeriod] = useState('daily');
//   const [pdfStartDate, setPdfStartDate] = useState('');
//   const [pdfEndDate, setPdfEndDate] = useState('');

//   const [excelPeriod, setExcelPeriod] = useState('daily');
//   const [excelStartDate, setExcelStartDate] = useState('');
//   const [excelEndDate, setExcelEndDate] = useState('');

//   const fetchReportData = async () => {
//     setLoading(true);
//     try {
//       const params = new URLSearchParams();
//       params.append('period', filterType);

//       if (filterType === 'daily' && selectedDate) {
//         params.append('on_date', selectedDate);
//       } else if (filterType === 'monthly') {
//         params.append('on_date', `${year}-${String(month).padStart(2, '0')}-01`);
//       } else if (filterType === 'yearly') {
//         params.append('on_date', `${year}-01-01`);
//       } else if (filterType === 'weekly' && selectedDate) {
//         params.append('on_date', selectedDate);
//       } else if (filterType === 'custom' && startDate && endDate) {
//         params.append('start_date', startDate);
//         params.append('end_date', endDate);
//       }

//       if (selectedChannel !== 'all') {
//         params.append('channel', selectedChannel);
//       }
//       if (selectedPaymentBucket !== 'all') {
//         params.append('payment_bucket', selectedPaymentBucket);
//       }
//       if (searchQuery) {
//         params.append('search', searchQuery);
//       }
//       params.append('include_cancelled', 'true');

//       const url = `${BQ_BASE_URL}/bq/api/restaurant/reports/dashboard?${params.toString()}`;
//       console.log('Fetching report from:', url);

//       const response = await fetch(url, {
//         headers: { accept: 'application/json' },
//         credentials: 'include',
//       });

//       if (response.ok) {
//         const data = await response.json();
//         console.log('Report data:', data);
//         setReportData(data);
//         setOrders(data.recent_orders || []);
//         setTotalOrders(data.recent_orders_total || 0);
//         toast.success('Report loaded successfully!');
//       } else {
//         const error = await response.json();
//         throw new Error(error.detail || 'Failed to fetch report');
//       }
//     } catch (error) {
//       console.error('Error fetching report:', error);
//       toast.error(error.message || 'Failed to load report');
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchReportData();
//   }, [filterType, selectedDate, year, month, selectedChannel, selectedPaymentBucket]);

//   const handlePeriodChange = (type) => {
//     setFilterType(type);
//     setCurrentPage(1);
//   };

//   const handleDateChange = (e) => {
//     setSelectedDate(e.target.value);
//     setCurrentPage(1);
//   };

//   const getFilteredOrders = () => {
//     let filtered = orders;
//     if (searchQuery) {
//       const query = searchQuery.toLowerCase();
//       filtered = filtered.filter(
//         (order) =>
//           (order.order_no || '').toLowerCase().includes(query) ||
//           (order.customer || '').toLowerCase().includes(query) ||
//           (order.table || '').toLowerCase().includes(query) ||
//           (order.room || '').toLowerCase().includes(query) ||
//           (order.kot_number || '').toLowerCase().includes(query)
//       );
//     }
//     return filtered;
//   };

//   const filteredOrders = getFilteredOrders();
//   const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
//   const startIndex = (currentPage - 1) * itemsPerPage;
//   const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

//   const handlePageChange = (page) => {
//     if (page >= 1 && page <= totalPages) setCurrentPage(page);
//   };

//   const formatCurrency = (amount) => {
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 0,
//       maximumFractionDigits: 0,
//     }).format(amount || 0);
//   };

//   const getPaymentDistribution = () => {
//     if (!reportData?.payment_distribution?.breakdown) return [];
//     return reportData.payment_distribution.breakdown
//       .filter((item) => item.amount > 0)
//       .map((item) => ({
//         name: item.label,
//         value: item.amount,
//         percentage: item.percentage,
//         color: PAYMENT_COLORS[item.bucket] || '#6b7280',
//         bg: PAYMENT_BG_COLORS[item.bucket] || '#f3f4f6',
//         key: item.bucket,
//       }));
//   };

//   const getDailyTracking = () => {
//     if (!reportData?.daily_tracking) return [];
//     return reportData.daily_tracking;
//   };

//   const getChannelSummary = () => {
//     if (!reportData?.channel_summary?.channels) return [];
//     return reportData.channel_summary.channels.filter((ch) => ch.revenue > 0 || ch.order_count > 0);
//   };

//   const getKPIs = () => {
//     if (!reportData?.kpis) return {};
//     return reportData.kpis;
//   };

//   const getTopItems = () => {
//     if (!reportData?.top_items) return [];
//     return reportData.top_items;
//   };

//   const getHourlySales = () => {
//     if (!reportData?.hourly_sales) return [];
//     return reportData.hourly_sales;
//   };

//   const handleExport = async (format) => {
//     setLoading(true);
//     try {
//       const params = new URLSearchParams();
//       params.append('period', filterType);
//       params.append('export_format', format);

//       if (filterType === 'daily' && selectedDate) {
//         params.append('on_date', selectedDate);
//       } else if (filterType === 'monthly') {
//         params.append('on_date', `${year}-${String(month).padStart(2, '0')}-01`);
//       } else if (filterType === 'yearly') {
//         params.append('on_date', `${year}-01-01`);
//       } else if (filterType === 'weekly' && selectedDate) {
//         params.append('on_date', selectedDate);
//       } else if (filterType === 'custom' && startDate && endDate) {
//         params.append('start_date', startDate);
//         params.append('end_date', endDate);
//       }

//       if (selectedChannel !== 'all') {
//         params.append('channel', selectedChannel);
//       }
//       params.append('include_cancelled', 'true');

//       const url = `${BQ_BASE_URL}/bq/api/restaurant/reports/export?${params.toString()}`;

//       const response = await fetch(url, {
//         headers: { accept: '*/*' },
//         credentials: 'include',
//       });

//       if (response.ok) {
//         const blob = await response.blob();
//         const downloadUrl = window.URL.createObjectURL(blob);
//         const a = document.createElement('a');
//         a.href = downloadUrl;
//         const contentDisposition = response.headers.get('content-disposition');
//         const filename = contentDisposition
//           ? contentDisposition.split('filename=')[1].replace(/"/g, '')
//           : `Restaurant_Report.${format}`;
//         a.download = filename;
//         document.body.appendChild(a);
//         a.click();
//         window.URL.revokeObjectURL(downloadUrl);
//         document.body.removeChild(a);
//         toast.success(`Report exported as ${format.toUpperCase()} successfully!`);
//       } else {
//         throw new Error('Failed to export report');
//       }
//     } catch (error) {
//       console.error('Export error:', error);
//       toast.error(error.message || 'Failed to export report');
//     } finally {
//       setLoading(false);
//       setShowPdfModal(false);
//       setShowExcelModal(false);
//     }
//   };

//   const handleSendEmail = async () => {
//     if (!emailRecipient) {
//       toast.error('Please enter recipient email');
//       return;
//     }

//     setSendingEmail(true);
//     try {
//       const payload = {
//         recipients: [emailRecipient],
//         subject: 'Restaurant Report',
//         message: 'Please find the restaurant report attached.',
//         export_format: exportFormat || 'xlsx',
//         period: emailPeriod,
//       };

//       if (emailPeriod === 'daily' && emailStartDate) {
//         payload.on_date = emailStartDate;
//       } else if (emailPeriod === 'weekly' && emailStartDate) {
//         payload.on_date = emailStartDate;
//       } else if (emailPeriod === 'monthly' && emailStartDate) {
//         payload.on_date = emailStartDate;
//       } else if (emailPeriod === 'custom' && emailStartDate && emailEndDate) {
//         payload.start_date = emailStartDate;
//         payload.end_date = emailEndDate;
//       }

//       if (selectedChannel !== 'all') {
//         payload.channel = selectedChannel;
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/reports/send-email`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           accept: 'application/json',
//         },
//         credentials: 'include',
//         body: JSON.stringify(payload),
//       });

//       if (response.ok) {
//         toast.success(`Report sent to ${emailRecipient} successfully!`);
//         setShowEmailModal(false);
//         setEmailRecipient('');
//         setEmailRecipientName('');
//       } else {
//         const error = await response.json();
//         throw new Error(error.detail || 'Failed to send email');
//       }
//     } catch (error) {
//       console.error('Email error:', error);
//       toast.error(error.message || 'Failed to send email');
//     } finally {
//       setSendingEmail(false);
//     }
//   };

//   const resetFilters = () => {
//     setSelectedChannel('all');
//     setSelectedPaymentBucket('all');
//     setSearchQuery('');
//     toast.info('Filters reset');
//   };

//   const kpis = getKPIs();
//   const paymentData = getPaymentDistribution();
//   const dailyData = getDailyTracking();
//   const channelData = getChannelSummary();
//   const topItems = getTopItems();
//   const hourlyData = getHourlySales();

//   // Get today's payment breakdown from daily tracking
//   const getTodayPaymentBreakdown = () => {
//     if (!dailyData || dailyData.length === 0) return [];
//     const today = dailyData[dailyData.length - 1];
//     if (!today) return [];

//     const breakdown = [];
//     PAYMENT_BUCKETS.forEach((bucket) => {
//       if (today[bucket] && today[bucket] > 0) {
//         breakdown.push({
//           key: bucket,
//           label: PAYMENT_LABEL[bucket],
//           amount: today[bucket],
//           color: PAYMENT_COLORS[bucket],
//           bg: PAYMENT_BG_COLORS[bucket],
//         });
//       }
//     });
//     return breakdown;
//   };

//   const todayPayments = getTodayPaymentBreakdown();

//   return (
//     <div className="min-h-screen bg-gray-50 p-4 md:p-6 font-sans">
//       <ToastContainer position="bottom-right" autoClose={3000} />

//       {/* Header */}
//       <div className="bg-gradient-to-r from-[#0a1628] to-[#1a2a4a] rounded-2xl shadow-lg p-6 mb-6">
//         <div className="flex flex-col md:flex-row md:items-center md:justify-between">
//           <div className="flex items-center gap-4">
//             <div className="w-12 h-12 bg-[#c9a84c]/20 backdrop-blur rounded-xl flex items-center justify-center">
//               <FaUtensils className="text-[#c9a84c] text-2xl" />
//             </div>
//             <div>
//               <h1 className="text-2xl font-bold text-white">Restaurant Report</h1>
//               <p className="text-[#c9a84c] text-sm">
//                 Complete analytics & insights for your restaurant operations
//               </p>
//               {reportData?.meta && (
//                 <p className="text-xs text-gray-400 mt-1">
//                   {reportData.meta.label} • {reportData.meta.start_date} to{' '}
//                   {reportData.meta.end_date}
//                 </p>
//               )}
//             </div>
//           </div>
//           <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
//             <button
//               onClick={() => {
//                 setExcelPeriod(filterType);
//                 setShowExcelModal(true);
//               }}
//               className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-sm font-medium"
//             >
//               <FaFileExcel /> Excel
//             </button>
//             <button
//               onClick={() => {
//                 setPdfPeriod(filterType);
//                 setShowPdfModal(true);
//               }}
//               className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
//             >
//               <FaFilePdf /> PDF
//             </button>
//             <button
//               onClick={() => {
//                 setEmailPeriod(filterType);
//                 setShowEmailModal(true);
//               }}
//               className="flex items-center gap-2 px-4 py-2 bg-[#c9a84c] text-[#0a1628] rounded-lg hover:bg-[#b8973a] transition text-sm font-medium"
//             >
//               <FaEnvelope /> Email
//             </button>
//             <button
//               onClick={fetchReportData}
//               disabled={loading}
//               className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition text-sm font-medium backdrop-blur"
//             >
//               <FaSync className={loading ? 'animate-spin' : ''} /> Refresh
//             </button>
//           </div>
//         </div>
//       </div>

//       {/* Filters Section */}
//       <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6">
//         <div className="flex flex-wrap items-center gap-3">
//           <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
//             {['daily', 'weekly', 'monthly', 'yearly', 'custom'].map((type) => (
//               <button
//                 key={type}
//                 onClick={() => handlePeriodChange(type)}
//                 className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition ${
//                   filterType === type
//                     ? 'bg-white shadow-sm text-[#0a1628]'
//                     : 'text-gray-600 hover:text-gray-900'
//                 }`}
//               >
//                 {type}
//               </button>
//             ))}
//           </div>

//           {filterType === 'daily' && (
//             <input
//               type="date"
//               value={selectedDate}
//               onChange={handleDateChange}
//               className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
//             />
//           )}

//           {filterType === 'weekly' && (
//             <input
//               type="date"
//               value={selectedDate}
//               onChange={handleDateChange}
//               className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
//             />
//           )}

//           {filterType === 'monthly' && (
//             <>
//               <select
//                 value={year}
//                 onChange={(e) => {
//                   setYear(parseInt(e.target.value));
//                   setCurrentPage(1);
//                 }}
//                 className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
//               >
//                 {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
//                   <option key={y} value={y}>
//                     {y}
//                   </option>
//                 ))}
//               </select>
//               <select
//                 value={month}
//                 onChange={(e) => {
//                   setMonth(parseInt(e.target.value));
//                   setCurrentPage(1);
//                 }}
//                 className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
//               >
//                 {[
//                   'Jan',
//                   'Feb',
//                   'Mar',
//                   'Apr',
//                   'May',
//                   'Jun',
//                   'Jul',
//                   'Aug',
//                   'Sep',
//                   'Oct',
//                   'Nov',
//                   'Dec',
//                 ].map((m, i) => (
//                   <option key={i} value={i + 1}>
//                     {m}
//                   </option>
//                 ))}
//               </select>
//             </>
//           )}

//           {filterType === 'yearly' && (
//             <select
//               value={year}
//               onChange={(e) => {
//                 setYear(parseInt(e.target.value));
//                 setCurrentPage(1);
//               }}
//               className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
//             >
//               {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
//                 <option key={y} value={y}>
//                   {y}
//                 </option>
//               ))}
//             </select>
//           )}

//           {filterType === 'custom' && (
//             <>
//               <input
//                 type="date"
//                 value={startDate}
//                 onChange={(e) => {
//                   setStartDate(e.target.value);
//                   setCurrentPage(1);
//                 }}
//                 className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
//               />
//               <span className="text-gray-500">to</span>
//               <input
//                 type="date"
//                 value={endDate}
//                 onChange={(e) => {
//                   setEndDate(e.target.value);
//                   setCurrentPage(1);
//                 }}
//                 className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
//               />
//             </>
//           )}

//           <div className="flex items-center gap-2 ml-auto">
//             <button
//               onClick={() => setShowFilterDropdown(!showFilterDropdown)}
//               className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition"
//             >
//               <FaFilter /> Filter
//               {(selectedChannel !== 'all' || selectedPaymentBucket !== 'all') && (
//                 <span className="w-2 h-2 bg-[#c9a84c] rounded-full animate-pulse"></span>
//               )}
//             </button>
//           </div>
//         </div>

//         {showFilterDropdown && (
//           <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//               <div>
//                 <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-2">
//                   Channel
//                 </label>
//                 <select
//                   value={selectedChannel}
//                   onChange={(e) => {
//                     setSelectedChannel(e.target.value);
//                     setCurrentPage(1);
//                   }}
//                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c] bg-white"
//                 >
//                   <option value="all">All Channels</option>
//                   {CHANNEL_ORDER.map((ch) => (
//                     <option key={ch} value={ch}>
//                       {CHANNEL_LABEL[ch] || ch}
//                     </option>
//                   ))}
//                 </select>
//               </div>
//               <div>
//                 <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-2">
//                   Payment Method
//                 </label>
//                 <select
//                   value={selectedPaymentBucket}
//                   onChange={(e) => {
//                     setSelectedPaymentBucket(e.target.value);
//                     setCurrentPage(1);
//                   }}
//                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c] bg-white"
//                 >
//                   <option value="all">All Methods</option>
//                   {PAYMENT_BUCKETS.map((bucket) => (
//                     <option key={bucket} value={bucket}>
//                       {PAYMENT_LABEL[bucket] || bucket}
//                     </option>
//                   ))}
//                 </select>
//               </div>
//               <div className="flex items-end">
//                 <button
//                   onClick={resetFilters}
//                   className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition flex items-center gap-2"
//                 >
//                   <FaTimes /> Clear All
//                 </button>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Loading State */}
//       {loading && !reportData ? (
//         <div className="flex justify-center items-center py-20">
//           <div className="text-center">
//             <div className="w-12 h-12 border-4 border-[#0a1628] border-t-[#c9a84c] rounded-full animate-spin mx-auto mb-4"></div>
//             <p className="text-gray-500">Loading report...</p>
//           </div>
//         </div>
//       ) : !reportData ? (
//         <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-200">
//           <FaChartLine className="text-5xl text-gray-300 mx-auto mb-4" />
//           <h3 className="text-xl font-semibold text-gray-700 mb-2">No Data Available</h3>
//           <p className="text-gray-400">Try adjusting your filters or refresh the page</p>
//         </div>
//       ) : (
//         <>
//           {/* ===== BEAUTIFUL KPI CARDS ===== */}
//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
//             {/* Total Revenue Card */}
//             <div className="bg-gradient-to-br from-[#0a1628] to-[#1a2a4a] rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
//               <div className="flex items-center justify-between">
//                 <div>
//                   <p className="text-xs font-medium text-[#c9a84c] uppercase tracking-wider">
//                     Total Revenue
//                   </p>
//                   <p className="text-2xl font-bold text-white mt-1">
//                     {formatCurrency(kpis.total_revenue || 0)}
//                   </p>
//                   {kpis.revenue_growth_pct !== null && (
//                     <p
//                       className={`text-xs mt-1 flex items-center gap-1 ${
//                         kpis.revenue_growth_pct >= 0 ? 'text-emerald-400' : 'text-red-400'
//                       }`}
//                     >
//                       {kpis.revenue_growth_pct >= 0 ? (
//                         <FaArrowUp size={10} />
//                       ) : (
//                         <FaArrowDown size={10} />
//                       )}
//                       {Math.abs(kpis.revenue_growth_pct)}% vs last period
//                     </p>
//                   )}
//                 </div>
//                 <div className="w-12 h-12 bg-[#c9a84c]/20 rounded-2xl flex items-center justify-center">
//                   <FaRupeeSign className="text-[#c9a84c] text-2xl" />
//                 </div>
//               </div>
//             </div>

//             {/* GST Collected Card */}
//             <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-all transform hover:-translate-y-1">
//               <div className="flex items-center justify-between">
//                 <div>
//                   <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     GST Collected
//                   </p>
//                   <p className="text-2xl font-bold text-gray-900 mt-1">
//                     {formatCurrency(kpis.gst_collected || 0)}
//                   </p>
//                   <p className="text-xs text-gray-400 mt-1">
//                     {kpis.gst_pct_of_revenue || 0}% of revenue
//                   </p>
//                 </div>
//                 <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
//                   <FaPercent className="text-purple-600 text-2xl" />
//                 </div>
//               </div>
//             </div>

//             {/* Total Orders Card */}
//             <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-all transform hover:-translate-y-1">
//               <div className="flex items-center justify-between">
//                 <div>
//                   <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Total Orders
//                   </p>
//                   <p className="text-2xl font-bold text-gray-900 mt-1">{kpis.total_orders || 0}</p>
//                   {kpis.orders_growth_pct !== null && (
//                     <p
//                       className={`text-xs mt-1 flex items-center gap-1 ${
//                         kpis.orders_growth_pct >= 0 ? 'text-emerald-600' : 'text-red-600'
//                       }`}
//                     >
//                       {kpis.orders_growth_pct >= 0 ? (
//                         <FaArrowUp size={10} />
//                       ) : (
//                         <FaArrowDown size={10} />
//                       )}
//                       {Math.abs(kpis.orders_growth_pct)}% vs last period
//                     </p>
//                   )}
//                 </div>
//                 <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center">
//                   <FaShoppingBag className="text-orange-600 text-2xl" />
//                 </div>
//               </div>
//             </div>

//             {/* Avg Order Value Card */}
//             <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-all transform hover:-translate-y-1">
//               <div className="flex items-center justify-between">
//                 <div>
//                   <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Avg Order Value
//                   </p>
//                   <p className="text-2xl font-bold text-gray-900 mt-1">
//                     {formatCurrency(kpis.avg_order_value || 0)}
//                   </p>
//                   <p className="text-xs text-gray-400 mt-1">
//                     {kpis.total_items_sold || 0} items sold
//                   </p>
//                 </div>
//                 <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center">
//                   <FaDollarSign className="text-emerald-600 text-2xl" />
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* ===== DAILY PAYMENT BREAKDOWN - FOCUS ON CASH, NETBANKING, UPI ===== */}
//           {todayPayments.length > 0 && (
//             <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
//               <div className="flex items-center justify-between mb-4">
//                 <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
//                   <FaCoins className="text-[#c9a84c]" />
//                   Today's Payment Breakdown
//                   <span className="text-xs font-normal text-gray-400 ml-2">
//                     {new Date(selectedDate).toLocaleDateString('en-IN', {
//                       day: '2-digit',
//                       month: 'short',
//                       year: 'numeric',
//                     })}
//                   </span>
//                 </h3>
//                 <span className="text-xs text-gray-500">
//                   Total: {formatCurrency(todayPayments.reduce((sum, p) => sum + p.amount, 0))}
//                 </span>
//               </div>

//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
//                 {todayPayments.map((payment) => (
//                   <div
//                     key={payment.key}
//                     className="rounded-xl p-4 border transition-all hover:shadow-md"
//                     style={{
//                       backgroundColor: payment.bg || '#f9fafb',
//                       borderColor: payment.color + '40',
//                     }}
//                   >
//                     <div className="flex items-center gap-3">
//                       <div
//                         className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
//                         style={{ backgroundColor: payment.color }}
//                       >
//                         {payment.key === 'cash' && <FaMoneyBillWave size={18} />}
//                         {payment.key === 'card' && <FaCreditCard size={18} />}
//                         {payment.key === 'netbanking' && <FaUniversity size={18} />}
//                         {payment.key === 'digital' && <FaMobileAlt size={18} />}
//                         {payment.key === 'upi' && <FaQrcode size={18} />}
//                         {payment.key === 'room_post' && <FaHotel size={18} />}
//                         {payment.key === 'other' && <FaWallet size={18} />}
//                       </div>
//                       <div>
//                         <p className="text-xs text-gray-500 font-medium">{payment.label}</p>
//                         <p className="text-lg font-bold text-gray-900">
//                           {formatCurrency(payment.amount)}
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           )}

//           {/* ===== PAYMENT METHODS SUMMARY CARDS ===== */}
//           <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
//             {paymentData.map((item) => (
//               <div
//                 key={item.key}
//                 className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-all transform hover:-translate-y-0.5"
//               >
//                 <div className="flex items-center gap-3">
//                   <div
//                     className="w-10 h-10 rounded-xl flex items-center justify-center"
//                     style={{ backgroundColor: item.bg || '#f3f4f6' }}
//                   >
//                     <span style={{ color: item.color }}>
//                       {item.key === 'cash' && <FaMoneyBillWave size={18} />}
//                       {item.key === 'card' && <FaCreditCard size={18} />}
//                       {item.key === 'netbanking' && <FaUniversity size={18} />}
//                       {item.key === 'digital' && <FaMobileAlt size={18} />}
//                       {item.key === 'upi' && <FaQrcode size={18} />}
//                       {item.key === 'room_post' && <FaHotel size={18} />}
//                       {item.key === 'other' && <FaWallet size={18} />}
//                     </span>
//                   </div>
//                   <div className="flex-1 min-w-0">
//                     <p className="text-xs text-gray-500 truncate">{item.name}</p>
//                     <p className="text-sm font-bold text-gray-900">{formatCurrency(item.value)}</p>
//                     <div className="flex items-center gap-1 mt-0.5">
//                       <div className="flex-1 bg-gray-100 rounded-full h-1">
//                         <div
//                           className="rounded-full h-1 transition-all"
//                           style={{
//                             width: `${Math.min(item.percentage, 100)}%`,
//                             backgroundColor: item.color,
//                           }}
//                         />
//                       </div>
//                       <span className="text-[10px] text-gray-400">{item.percentage}%</span>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>

//           {/* Beautiful Charts Section */}
//           <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
//             {/* Revenue Trend - Area Chart */}
//             <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
//               <div className="flex items-center justify-between mb-4">
//                 <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
//                   <FaChartLine className="text-[#c9a84c]" />
//                   Revenue Trend
//                 </h3>
//                 <div className="flex gap-1 bg-gray-100 p-0.5 rounded-lg">
//                   <button
//                     onClick={() => setActiveChartTab('trend')}
//                     className={`px-2 py-0.5 text-xs rounded transition ${activeChartTab === 'trend' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
//                   >
//                     Trend
//                   </button>
//                   <button
//                     onClick={() => setActiveChartTab('hourly')}
//                     className={`px-2 py-0.5 text-xs rounded transition ${activeChartTab === 'hourly' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
//                   >
//                     Hourly
//                   </button>
//                 </div>
//               </div>
//               <div className="h-64">
//                 <ResponsiveContainer width="100%" height="100%">
//                   {activeChartTab === 'trend' ? (
//                     <AreaChart data={dailyData}>
//                       <defs>
//                         <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
//                           <stop offset="5%" stopColor="#c9a84c" stopOpacity={0.3} />
//                           <stop offset="95%" stopColor="#c9a84c" stopOpacity={0} />
//                         </linearGradient>
//                       </defs>
//                       <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
//                       <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
//                       <YAxis
//                         tickFormatter={(v) => `₹${v / 1000}K`}
//                         stroke="#94a3b8"
//                         fontSize={11}
//                       />
//                       <Tooltip formatter={(value) => formatCurrency(value)} />
//                       <Area
//                         type="monotone"
//                         dataKey="total"
//                         stroke="#c9a84c"
//                         strokeWidth={3}
//                         fill="url(#revenueGradient)"
//                         name="Revenue"
//                       />
//                     </AreaChart>
//                   ) : (
//                     <BarChart data={hourlyData}>
//                       <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
//                       <XAxis dataKey="label" stroke="#94a3b8" fontSize={10} />
//                       <YAxis
//                         tickFormatter={(v) => `₹${v / 1000}K`}
//                         stroke="#94a3b8"
//                         fontSize={11}
//                       />
//                       <Tooltip formatter={(value) => formatCurrency(value)} />
//                       <Bar dataKey="revenue" fill="#c9a84c" radius={[4, 4, 0, 0]} name="Revenue" />
//                     </BarChart>
//                   )}
//                 </ResponsiveContainer>
//               </div>
//             </div>

//             {/* Payment Distribution - Donut Chart */}
//             <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
//               <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
//                 <FaChartPie className="text-[#c9a84c]" />
//                 Payment Distribution
//               </h3>
//               <div className="flex flex-col lg:flex-row items-center">
//                 <div className="w-full lg:w-1/2 h-56">
//                   <ResponsiveContainer width="100%" height="100%">
//                     <PieChart>
//                       <Pie
//                         data={paymentData}
//                         cx="50%"
//                         cy="50%"
//                         innerRadius={50}
//                         outerRadius={75}
//                         paddingAngle={3}
//                         dataKey="value"
//                       >
//                         {paymentData.map((entry, index) => (
//                           <Cell
//                             key={`cell-${index}`}
//                             fill={entry.color}
//                             stroke="white"
//                             strokeWidth={2}
//                           />
//                         ))}
//                       </Pie>
//                       <Tooltip formatter={(value) => formatCurrency(value)} />
//                     </PieChart>
//                   </ResponsiveContainer>
//                 </div>
//                 <div className="w-full lg:w-1/2 space-y-1.5">
//                   {paymentData.map((item) => (
//                     <div key={item.name} className="flex items-center justify-between text-sm">
//                       <div className="flex items-center gap-2">
//                         <span
//                           className="w-3 h-3 rounded-full"
//                           style={{ backgroundColor: item.color }}
//                         />
//                         <span className="text-gray-600">{item.name}</span>
//                       </div>
//                       <div className="flex items-center gap-3">
//                         <span className="font-medium text-gray-900">
//                           {formatCurrency(item.value)}
//                         </span>
//                         <span className="text-xs text-gray-400">{item.percentage}%</span>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Daily Payment Tracking - Stacked Bar */}
//           <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
//             <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
//               <FaChartBar className="text-[#c9a84c]" />
//               Daily Payment Tracking
//             </h3>
//             <div className="h-64">
//               <ResponsiveContainer width="100%" height="100%">
//                 <BarChart data={dailyData}>
//                   <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
//                   <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
//                   <YAxis tickFormatter={(v) => `₹${v / 1000}K`} stroke="#94a3b8" fontSize={11} />
//                   <Tooltip formatter={(value) => formatCurrency(value)} />
//                   <Legend wrapperStyle={{ fontSize: '11px' }} />
//                   {PAYMENT_BUCKETS.filter((b) => dailyData.some((d) => d[b] > 0)).map((bucket) => (
//                     <Bar
//                       key={bucket}
//                       dataKey={bucket}
//                       stackId="a"
//                       fill={PAYMENT_COLORS[bucket]}
//                       name={PAYMENT_LABEL[bucket]}
//                       radius={[4, 4, 0, 0]}
//                     />
//                   ))}
//                 </BarChart>
//               </ResponsiveContainer>
//             </div>
//           </div>

//           {/* Channel Performance */}
//           {channelData.length > 0 && (
//             <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
//               <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
//                 <FaChartBar className="text-[#c9a84c]" />
//                 Channel Performance
//               </h3>
//               <div className="overflow-x-auto">
//                 <table className="w-full text-sm">
//                   <thead className="bg-gray-50">
//                     <tr>
//                       <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
//                         Channel
//                       </th>
//                       <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
//                         Orders
//                       </th>
//                       <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
//                         Revenue
//                       </th>
//                       <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
//                         Tax
//                       </th>
//                       <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
//                         Discount
//                       </th>
//                       <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
//                         AOV
//                       </th>
//                       <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
//                         Share
//                       </th>
//                     </tr>
//                   </thead>
//                   <tbody className="divide-y divide-gray-100">
//                     {channelData.map((channel) => (
//                       <tr key={channel.channel} className="hover:bg-gray-50 transition">
//                         <td className="px-4 py-2.5 font-medium text-gray-900 flex items-center gap-2">
//                           <span
//                             className="w-2.5 h-2.5 rounded-full"
//                             style={{
//                               backgroundColor: CHANNEL_COLORS[channel.channel] || '#6b7280',
//                             }}
//                           />
//                           {channel.label}
//                         </td>
//                         <td className="px-4 py-2.5 text-right">{channel.order_count}</td>
//                         <td className="px-4 py-2.5 text-right font-semibold">
//                           {formatCurrency(channel.revenue)}
//                         </td>
//                         <td className="px-4 py-2.5 text-right">
//                           {formatCurrency(channel.tax_amount)}
//                         </td>
//                         <td className="px-4 py-2.5 text-right text-green-600">
//                           {formatCurrency(channel.discount)}
//                         </td>
//                         <td className="px-4 py-2.5 text-right">
//                           {formatCurrency(channel.avg_order_value)}
//                         </td>
//                         <td className="px-4 py-2.5 text-right">
//                           <span className="bg-gray-100 px-2 py-1 rounded-full text-xs font-medium">
//                             {channel.revenue_share_pct}%
//                           </span>
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </div>
//           )}

//           {/* Top Items */}
//           {topItems.length > 0 && (
//             <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
//               <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
//                 <FaTrophy className="text-[#c9a84c]" />
//                 Top Selling Items
//               </h3>
//               <div className="overflow-x-auto">
//                 <table className="w-full text-sm">
//                   <thead className="bg-gray-50">
//                     <tr>
//                       <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
//                         #
//                       </th>
//                       <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
//                         Item
//                       </th>
//                       <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
//                         Qty
//                       </th>
//                       <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
//                         Revenue
//                       </th>
//                       <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
//                         Tax
//                       </th>
//                       <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
//                         Channels
//                       </th>
//                     </tr>
//                   </thead>
//                   <tbody className="divide-y divide-gray-100">
//                     {topItems.slice(0, 10).map((item, index) => (
//                       <tr key={index} className="hover:bg-gray-50 transition">
//                         <td className="px-4 py-2.5">
//                           <span
//                             className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
//                               index === 0
//                                 ? 'bg-[#c9a84c] text-white'
//                                 : index === 1
//                                   ? 'bg-gray-300 text-gray-700'
//                                   : index === 2
//                                     ? 'bg-amber-600 text-white'
//                                     : 'bg-gray-100 text-gray-500'
//                             }`}
//                           >
//                             {index + 1}
//                           </span>
//                         </td>
//                         <td className="px-4 py-2.5 font-medium text-gray-900">{item.item}</td>
//                         <td className="px-4 py-2.5 text-right font-medium">{item.quantity}</td>
//                         <td className="px-4 py-2.5 text-right font-semibold">
//                           {formatCurrency(item.revenue)}
//                         </td>
//                         <td className="px-4 py-2.5 text-right">
//                           {formatCurrency(item.tax_amount)}
//                         </td>
//                         <td className="px-4 py-2.5">
//                           <div className="flex gap-1 flex-wrap">
//                             {item.channels.map((ch) => (
//                               <span
//                                 key={ch}
//                                 className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600"
//                               >
//                                 {CHANNEL_LABEL[ch] || ch}
//                               </span>
//                             ))}
//                           </div>
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </div>
//           )}

//           {/* Recent Orders Table */}
//           <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
//             <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
//               <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
//                 <FaList className="text-[#c9a84c]" />
//                 Recent Orders
//               </h3>
//               <div className="flex items-center gap-3">
//                 <div className="relative">
//                   <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
//                   <input
//                     type="text"
//                     placeholder="Search orders..."
//                     value={searchQuery}
//                     onChange={(e) => {
//                       setSearchQuery(e.target.value);
//                       setCurrentPage(1);
//                     }}
//                     className="pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]"
//                   />
//                 </div>
//                 <span className="text-xs text-gray-500">
//                   {filteredOrders.length} of {totalOrders} orders
//                 </span>
//               </div>
//             </div>

//             <div className="overflow-x-auto">
//               <table className="w-full text-sm">
//                 <thead className="bg-gray-50">
//                   <tr>
//                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
//                       Order
//                     </th>
//                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
//                       Customer
//                     </th>
//                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
//                       Channel
//                     </th>
//                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
//                       Table/Room
//                     </th>
//                     <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
//                       Total
//                     </th>
//                     <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
//                       Tax
//                     </th>
//                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
//                       Payment
//                     </th>
//                     <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
//                       Status
//                     </th>
//                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
//                       Time
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-100">
//                   {paginatedOrders.length === 0 ? (
//                     <tr>
//                       <td colSpan="9" className="text-center py-8 text-gray-500">
//                         No orders found
//                       </td>
//                     </tr>
//                   ) : (
//                     paginatedOrders.map((order) => (
//                       <tr key={order.order_id} className="hover:bg-gray-50 transition">
//                         <td className="px-4 py-2.5">
//                           <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">
//                             {order.order_no || order.order_id?.slice(0, 8)}
//                           </span>
//                           {order.kot_number && (
//                             <div className="text-xs text-gray-400">KOT: {order.kot_number}</div>
//                           )}
//                         </td>
//                         <td className="px-4 py-2.5">
//                           <div className="font-medium text-gray-900">
//                             {order.customer || 'Guest'}
//                           </div>
//                           {order.customer_phone && (
//                             <div className="text-xs text-gray-500">{order.customer_phone}</div>
//                           )}
//                         </td>
//                         <td className="px-4 py-2.5">
//                           <span
//                             className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
//                             style={{
//                               backgroundColor: CHANNEL_COLORS[order.channel] || '#6b7280',
//                             }}
//                           >
//                             {order.channel_label || order.channel}
//                           </span>
//                         </td>
//                         <td className="px-4 py-2.5">
//                           {order.table || order.room || order.parcel_number || '-'}
//                         </td>
//                         <td className="px-4 py-2.5 text-right font-semibold">
//                           {formatCurrency(order.total)}
//                         </td>
//                         <td className="px-4 py-2.5 text-right text-gray-500">
//                           {formatCurrency(order.tax)}
//                         </td>
//                         <td className="px-4 py-2.5">
//                           <span className="flex items-center gap-1.5 text-gray-700">
//                             <span
//                               className="w-2 h-2 rounded-full"
//                               style={{
//                                 backgroundColor: PAYMENT_COLORS[order.payment_bucket] || '#6b7280',
//                               }}
//                             />
//                             {order.payment_label || 'Not Paid'}
//                           </span>
//                         </td>
//                         <td className="px-4 py-2.5 text-center">
//                           {order.is_cancelled ? (
//                             <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
//                               Cancelled
//                             </span>
//                           ) : order.is_paid ? (
//                             <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
//                               Paid
//                             </span>
//                           ) : (
//                             <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
//                               {order.status || 'Pending'}
//                             </span>
//                           )}
//                         </td>
//                         <td className="px-4 py-2.5 text-gray-500 text-xs">
//                           {order.ordered_at ? new Date(order.ordered_at).toLocaleString() : '-'}
//                         </td>
//                       </tr>
//                     ))
//                   )}
//                 </tbody>
//               </table>
//             </div>

//             {totalPages > 0 && (
//               <div className="px-5 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
//                 <p className="text-xs text-gray-500">
//                   Page {currentPage} of {totalPages}
//                 </p>
//                 <div className="flex gap-1">
//                   <button
//                     onClick={() => handlePageChange(currentPage - 1)}
//                     disabled={currentPage === 1}
//                     className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition text-sm"
//                   >
//                     Previous
//                   </button>
//                   {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
//                     const page = i + 1;
//                     return (
//                       <button
//                         key={page}
//                         onClick={() => handlePageChange(page)}
//                         className={`px-3 py-1.5 rounded-lg text-sm transition ${
//                           currentPage === page
//                             ? 'bg-[#0a1628] text-white'
//                             : 'border border-gray-300 hover:bg-gray-50'
//                         }`}
//                       >
//                         {page}
//                       </button>
//                     );
//                   })}
//                   {totalPages > 5 && <span className="px-2 py-1.5 text-gray-400">...</span>}
//                   <button
//                     onClick={() => handlePageChange(currentPage + 1)}
//                     disabled={currentPage === totalPages}
//                     className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition text-sm"
//                   >
//                     Next
//                   </button>
//                 </div>
//               </div>
//             )}
//           </div>
//         </>
//       )}

//       {/* ========== MODALS ========== */}

//       {/* PDF Modal */}
//       {showPdfModal && (
//         <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
//           <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
//             <div className="flex justify-between items-center mb-4">
//               <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
//                 <FaFilePdf className="text-red-600" />
//                 Download PDF Report
//               </h2>
//               <button
//                 onClick={() => setShowPdfModal(false)}
//                 className="text-gray-400 hover:text-gray-600 transition p-1"
//               >
//                 <FaTimes size={20} />
//               </button>
//             </div>
//             <p className="text-sm text-gray-600 mb-4">
//               Download the complete restaurant report as a PDF document.
//             </p>
//             <div className="flex gap-3">
//               <button
//                 onClick={() => setShowPdfModal(false)}
//                 className="flex-1 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition text-sm font-medium text-gray-700"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={() => handleExport('pdf')}
//                 disabled={loading}
//                 className="flex-1 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50"
//               >
//                 {loading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
//                 {loading ? 'Generating...' : 'Download PDF'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Excel Modal */}
//       {showExcelModal && (
//         <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
//           <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
//             <div className="flex justify-between items-center mb-4">
//               <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
//                 <FaFileExcel className="text-emerald-600" />
//                 Download Excel Report
//               </h2>
//               <button
//                 onClick={() => setShowExcelModal(false)}
//                 className="text-gray-400 hover:text-gray-600 transition p-1"
//               >
//                 <FaTimes size={20} />
//               </button>
//             </div>
//             <p className="text-sm text-gray-600 mb-4">
//               Download the complete restaurant report as an Excel spreadsheet.
//             </p>
//             <div className="flex gap-3">
//               <button
//                 onClick={() => setShowExcelModal(false)}
//                 className="flex-1 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition text-sm font-medium text-gray-700"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={() => handleExport('xlsx')}
//                 disabled={loading}
//                 className="flex-1 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50"
//               >
//                 {loading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
//                 {loading ? 'Generating...' : 'Download Excel'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Email Modal */}
//       {showEmailModal && (
//         <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
//           <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
//             <div className="flex justify-between items-center mb-4">
//               <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
//                 <FaEnvelope className="text-[#c9a84c]" />
//                 Send Report via Email
//               </h2>
//               <button
//                 onClick={() => setShowEmailModal(false)}
//                 className="text-gray-400 hover:text-gray-600 transition p-1"
//               >
//                 <FaTimes size={20} />
//               </button>
//             </div>
//             <div className="space-y-4">
//               <div>
//                 <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
//                   Recipient Email *
//                 </label>
//                 <input
//                   type="email"
//                   placeholder="Enter email address"
//                   value={emailRecipient}
//                   onChange={(e) => setEmailRecipient(e.target.value)}
//                   className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
//                 />
//               </div>
//               <div>
//                 <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
//                   Recipient Name
//                 </label>
//                 <input
//                   type="text"
//                   placeholder="Enter recipient name"
//                   value={emailRecipientName}
//                   onChange={(e) => setEmailRecipientName(e.target.value)}
//                   className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
//                 />
//               </div>
//               <div>
//                 <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
//                   Report Period
//                 </label>
//                 <select
//                   value={emailPeriod}
//                   onChange={(e) => setEmailPeriod(e.target.value)}
//                   className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
//                 >
//                   <option value="daily">Daily</option>
//                   <option value="weekly">Weekly</option>
//                   <option value="monthly">Monthly</option>
//                   <option value="yearly">Yearly</option>
//                   <option value="custom">Custom Range</option>
//                 </select>
//               </div>
//               {emailPeriod === 'custom' && (
//                 <div className="grid grid-cols-2 gap-3">
//                   <div>
//                     <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
//                       Start Date
//                     </label>
//                     <input
//                       type="date"
//                       value={emailStartDate}
//                       onChange={(e) => setEmailStartDate(e.target.value)}
//                       className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
//                     />
//                   </div>
//                   <div>
//                     <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
//                       End Date
//                     </label>
//                     <input
//                       type="date"
//                       value={emailEndDate}
//                       onChange={(e) => setEmailEndDate(e.target.value)}
//                       className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
//                     />
//                   </div>
//                 </div>
//               )}
//               <div>
//                 <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">
//                   Export Format
//                 </label>
//                 <select
//                   value={exportFormat}
//                   onChange={(e) => setExportFormat(e.target.value)}
//                   className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:border-transparent text-sm"
//                 >
//                   <option value="xlsx">Excel (XLSX)</option>
//                   <option value="pdf">PDF</option>
//                   <option value="csv">CSV</option>
//                 </select>
//               </div>
//               <div className="flex items-center gap-3 pt-2">
//                 <button
//                   type="button"
//                   onClick={() => setEmailCcHotel(!emailCcHotel)}
//                   className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
//                     emailCcHotel ? 'bg-[#0a1628] text-white' : 'bg-gray-100 text-gray-500'
//                   }`}
//                 >
//                   {emailCcHotel ? '✓' : '○'} CC Hotel
//                 </button>
//               </div>
//             </div>
//             <div className="flex gap-3 mt-6">
//               <button
//                 onClick={() => setShowEmailModal(false)}
//                 className="flex-1 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition text-sm font-medium text-gray-700"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleSendEmail}
//                 disabled={sendingEmail}
//                 className="flex-1 py-3 bg-[#0a1628] text-white rounded-xl hover:bg-[#1a2a4a] transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50"
//               >
//                 {sendingEmail ? <FaSpinner className="animate-spin" /> : <FaEnvelope />}
//                 {sendingEmail ? 'Sending...' : 'Send Report'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default RestaurantReport;
